import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface DropdownOption {
  value: string;
  label: string;
  subtitle?: string;
}

interface SearchableDropdownProps {
  value: string | null;
  onChange: (value: string | null) => void;
  type?: never;
  options?: DropdownOption[]; // For custom options format
  items?: any[]; // For backward compatibility
  displayField?: string; // Field to use for display (default: 'name')
  valueField?: string; // Field to use for value (default: 'id')
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  required?: boolean;
  error?: string;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  value,
  onChange,
  type,
  options: customOptions,
  items,
  displayField = 'name',
  valueField = 'id',
  placeholder = type ? `Select ${type}...` : 'Select...',
  disabled = false,
  className = '',
  required: _required = false,
  error: _error
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const [selectedLabel, setSelectedLabel] = useState<string>('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Since we removed companies, this component now only works with custom options
  const isLoading = false;

  // Determine if we're using custom options or items
  const usingCustomOptions = customOptions !== undefined;
  const usingItems = items !== undefined && !usingCustomOptions;

  // Process options based on whether custom or fetched - memoized to avoid dependency issues
  const options = useMemo(() => {
    if (usingCustomOptions) {
      // Filter custom options based on search term
      return (customOptions || []).filter(opt =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (opt.subtitle && opt.subtitle.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    } else if (usingItems) {
      // Filter items based on display field
      return (items || []).filter(item => {
        const displayValue = item[displayField] || '';
        return displayValue.toString().toLowerCase().includes(searchTerm.toLowerCase());
      });
    } else {
      // No options provided
      return [];
    }
  }, [usingCustomOptions, customOptions, searchTerm, usingItems, items, displayField]);

  // Effect to fetch and set selected label
  useEffect(() => {
    if (usingCustomOptions) {
      // Find selected label from custom options
      const selectedOption = (customOptions || []).find(opt => opt.value === value);
      setSelectedLabel(selectedOption?.label || '');
    } else if (usingItems && value) {
      // Find selected label from items
      const selectedItem = (items || []).find(item => item[valueField] === value);
      if (selectedItem) {
        setSelectedLabel(selectedItem[displayField] || '');
      } else {
        setSelectedLabel('');
      }
    } else if (value && type) {
      // Get selected item label - find in options first, then use fetched data
      const existingOption = options.find((opt: any) => opt.id === value);
      if (existingOption) {
        setSelectedLabel(existingOption.name);
      } else {
        setSelectedLabel('Loading...');
      }
    } else {
      setSelectedLabel('');
    }
  }, [value, usingCustomOptions, customOptions, usingItems, items, displayField, valueField, type, options]);

  // Close dropdown when clicking outside and check position
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    // Check if dropdown should open upwards
    const checkDropdownPosition = () => {
      if (buttonRef.current && isOpen) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;
        
        // If not enough space below and more space above, open upwards
        if (spaceBelow < 300 && spaceAbove > spaceBelow) {
          setDropdownPosition('top');
        } else {
          setDropdownPosition('bottom');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    if (isOpen) {
      checkDropdownPosition();
    }
    
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setSearchTerm('');
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="relative w-full bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-50 disabled:cursor-not-allowed"
      >
        <span className="block truncate">
          {selectedLabel || placeholder}
        </span>
        <span className="absolute inset-y-0 right-0 flex items-center pr-2">
          {value && !disabled ? (
            <XMarkIcon
              className="h-5 w-5 text-gray-400 hover:text-gray-500"
              onClick={handleClear}
            />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          )}
        </span>
      </button>

      {isOpen && !disabled && (
        <div 
          className={`absolute z-50 w-full min-w-[250px] bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm ${
            dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
          style={{ minWidth: '250px' }}>
          <div className="sticky top-0 z-10 bg-white px-2 py-1.5">
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder={usingCustomOptions ? 'Search...' : usingItems ? `Search...` : `Search ${type}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {isLoading && !usingCustomOptions ? (
            <div className="px-4 py-2 text-sm text-gray-500">Loading...</div>
          ) : options.length === 0 ? (
            <div className="px-4 py-2 text-sm text-gray-500">
              No {usingCustomOptions ? 'options' : usingItems ? 'items' : 'companies'} found
            </div>
          ) : (
            <div className="max-h-48 overflow-y-auto">
              {options.map((option: any) => {
                // Handle both custom options and items format
                const optionId = usingCustomOptions ? option.value : usingItems ? option[valueField] : option.id;
                const optionLabel = usingCustomOptions ? option.label : usingItems ? option[displayField] : option.name;
                const optionSubtitle = usingCustomOptions ? option.subtitle : usingItems ? null : option.type;
                
                return (
                  <div
                    key={optionId}
                    onClick={() => handleSelect(optionId)}
                    className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-50 ${
                      value === optionId ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                    }`}
                  >
                    <div>
                      <span className="block truncate">
                        {optionLabel}
                      </span>
                      {optionSubtitle && (
                        <span className="block text-xs text-gray-500 truncate">
                          {optionSubtitle}
                        </span>
                      )}
                    </div>
                    {value === optionId && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                        ✓
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;